<script lang="ts" setup>
import BaseDialog from '@/components/dialogs/BaseDialog.vue'
import SkrCheckbox from '@/components/SkrCheckbox.vue'

const props = withDefaults(
  defineProps<{
    loading?: boolean
    showZertes: boolean
    showEidas: boolean
    context: TermsContext
  }>(),
  {
    loading: false,
  }
)

const emit = defineEmits<{
  action: []
  close: []
}>()

const { t } = useI18n()
const agreedToTerms = ref(false)
const { invitingBusinessId } = storeToRefs(useDirectSignStore())
const { rootStyle } = useIdentificationTheme(invitingBusinessId)

const isNgri2 = computed(() => props.showEidas && props.showZertes)
const dialogSubtitle = computed(() =>
  isNgri2.value ? t('viewer.legal.overlay.text_identification') : t('viewer.legal.overlay.text')
)

const { track } = useAnalytics()

onMounted(() => {
  track(ANALYTICS_EVENTS.TC_MODAL_VIEWED, { context: props.context })
})

const onDismiss = () => {
  track(ANALYTICS_EVENTS.TC_MODAL_DISMISSED, { context: props.context })
  emit('close')
}
</script>

<template>
  <base-dialog
    data-cy="terms_conditions_dialog"
    class="tc-dialog"
    :title="t('viewer.legal.overlay.xqes_dialog.title')"
    :disabled="!agreedToTerms"
    :loading="loading"
    :action-text="t('viewer.legal.overlay.xqes_dialog.accept_tou')"
    :cancel-text="t('viewer.legal.overlay.xqes_dialog.decline_tou')"
    surface-variant
    card-class="identification-typography"
    :card-style="rootStyle"
    @action="emit('action')"
    @close="onDismiss"
  >
    <div class="tc-dialog__container">
      <div class="tc-dialog__terms">
        <p class="tc-dialog__subtitle">
          {{ dialogSubtitle }}
        </p>
        <ul class="tc-dialog__nested-tou">
          <li class="tc-dialog__tou">
            <a :href="t('viewer.legal.overlay.xqes_dialog.skribble_terms.link')" target="_blank" rel="noopener">
              {{ t('viewer.legal.overlay.xqes_dialog.skribble_terms.text') }}
            </a>
          </li>
          <li v-if="showZertes" class="tc-dialog__tou">
            <a :href="t('viewer.legal.overlay.xqes_dialog.zertes_terms.link')" target="_blank" rel="noopener">
              {{ t('viewer.legal.overlay.xqes_dialog.zertes_terms.text') }}
            </a>
          </li>
          <li v-if="showEidas" class="tc-dialog__tou">
            <a :href="t('viewer.legal.overlay.xqes_dialog.eidas_terms.link')" target="_blank" rel="noopener">
              {{ t('viewer.legal.overlay.xqes_dialog.eidas_terms.text') }}
            </a>
          </li>
        </ul>
      </div>
      <small class="tc-dialog__privacy-policy">
        <i18n-t keypath="viewer.legal.overlay.privacy_policy.label_text" tag="div" :plural="3">
          <a :href="t('viewer.legal.overlay.privacy_policy.skribble.label_link_url')" target="_blank" rel="noopener">
            {{ t('viewer.legal.overlay.privacy_policy.skribble.label_link_text') }}
          </a>
          <a :href="t('viewer.legal.overlay.privacy_policy.pxl_vision.label_link_url')" target="_blank" rel="noopener">
            {{ t('viewer.legal.overlay.privacy_policy.pxl_vision.label_link_text') }}
          </a>
          <a :href="t('viewer.legal.overlay.privacy_policy.swisscom.label_link_url')" target="_blank" rel="noopener">
            {{ t('viewer.legal.overlay.privacy_policy.swisscom.label_link_text') }}
          </a>
        </i18n-t>
      </small>
      <div class="tc-dialog__footer">
        <v-divider />
        <skr-checkbox v-model="agreedToTerms" class="tc-dialog__checkbox">
          {{ t('viewer.legal.overlay.xqes_dialog.parent_checkbox') }}
        </skr-checkbox>
      </div>
    </div>
  </base-dialog>
</template>

<style lang="scss" scoped>
.tc-dialog {
  &__container {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 8 * $bw;
    color: $c-skribbleu;
    font-size: 1rem;
  }

  &__footer {
    // the negative margins are required to make the v-divider "full-width" and override Vuetify's padding
    margin-left: -2rem;
    margin-right: -2rem;
  }

  &__checkbox {
    display: flex;
    justify-content: flex-start;
    padding-inline: 8 * $bw;
    align-items: center;
    padding-top: 1rem;
  }

  &__nested-tou {
    padding-left: 6 * $bw;
    margin-left: 5px;
    margin-top: 10px;

    li {
      margin-top: 1.25rem;
    }

    a,
    a:visited {
      color: rgb(var(--v-theme-on-surface-xqes)) !important;
    }

    .skr-checkbox__label--active a:visited {
      color: inherit;
    }
  }

  &__privacy-policy {
    font-size: 0.875rem;
    color: $c-grey-dark;

    a,
    a:visited {
      color: rgb(var(--v-theme-primary-xqes)) !important;
    }
  }
}
</style>
